/* Meeting Links (stored as CTAs): the links her copy points at, in one place so
 * a change to a URL reaches every page that uses it.
 *
 * THROUGH THE SCREEN PATTERN 2026-09-23 — the last panel that was not. It had a
 * Save button, a Cancel, and "Nothing saves until you save it". It is shaped
 * like the Offer Ladder, the other flat panel: no Level 1, so the Table / Link
 * toggle stays a genuine toggle (CLAUDE.md 9b) — both views show the same set,
 * one as a list and one as the link she is editing with the rest in the rail.
 */
import { useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { CalendarDays } from "lucide-react";
import { useCrmConnected } from "./mediaPicker";
import { CTA_STATUS_LABEL, CTA_TYPES, calendarPatch, ctaLabel, ctaProblemSummary, ctaProblems, ctaStatus, ctaType, ctaUseSummary, ctaUses } from "../../utils/useCtas";
import { B, input, nb, pDesc, pTitle } from "./tokens";
import { AddButton, Badge, Bread, Choice, Field, Hint, Menu2, Status, useTwoPane } from "./ui";
import { BarBack, L1Table, MomentumBar, RecordCopy, RecordFooter } from "./shell";

export const CTA_COLS = "1.7fr 1.8fr 1.1fr .85fr 34px";

/* What the panel is — visible on the landing screen, behind the crumb's (i) on
   a link (CLAUDE.md 9e). Kept to what she cannot see by looking: that the URL
   lives elsewhere, and that one edit reaches everything that points here. */
export const CTA_HINT = <>Each meeting link is two parts: the <b style={nb}>button text</b> people click and the <b style={nb}>URL it links to</b> (hosted in GHL, your CRM, or your website — not here). Your offers, sites, sequences and posts all point at the same ones, so changing a link changes it everywhere.</>;

/* One dot, one meaning, on the table and in the rail: teal when the link works
   and is live, amber while it is a draft or has no URL — "No URL yet" is the
   thing most worth her attention on this screen. */
const ctaDot = (c) => (ctaStatus(c) === "live" ? B.teal : B.amberFill);


export function CtaLibrary({ ctas, offers = [], loading, error, onDismissError, menuOpen, setMenuOpen, openForm, onOpen, onDelete, view, setView }) {
  /* Offers are the first thing that genuinely references a meeting link, so
     "Used in" is a real lookup. Sequences, pages and posts join `ctaUses`'s
     sources as they land. */
  const problems = ctaProblemSummary(ctaProblems(ctas, (c) => ctaUses(c.id, { offers }).total));

  return (
    <>
      <Bread text="Run" />
      <h2 style={pTitle}>Meeting Links</h2>
      <p style={pDesc}>{CTA_HINT}</p>

      <MomentumBar view={view} setView={setView} copyLabel="Link" add={
        <div style={{ position: "relative", display: "inline-block" }}>
          <AddButton label="Add meeting link" onClick={() => setMenuOpen((o) => !o)} />
          {menuOpen && (
            <Menu2 head="Where does it send them?" items={CTA_TYPES.map((t) => [
              t.icon, t.label, t.hint, false, () => openForm(t.id),
            ])} />
          )}
        </div>
      } />

      {error && (
        <div onClick={onDismissError} style={{ background: "var(--danger-quiet)", border: "1px solid var(--danger-line)", borderRadius: 9, padding: "11px 14px", marginBottom: 16, fontSize: 13, color: "var(--danger-text)", cursor: "pointer" }}>
          {error}
        </div>
      )}

      {/* Only shown when something is actually wrong. A permanent "all good" line
          is noise; a line that appears is the signal. */}
      {problems && (
        <div style={{ fontSize: 12, color: "var(--warn-text)", background: "var(--warn-bg)", border: "1px solid var(--warn-line)", borderRadius: 8, padding: "7px 11px", marginBottom: 12, display: "inline-block" }}>
          {problems}
        </div>
      )}

      <L1Table
        records={ctas} cols={CTA_COLS}
        labels={["Link", "Sends them to", "Used in", "Status", ""]}
        loading={loading} loadingText="Loading your meeting links…"
        empty="No meeting links yet. Add one and everything that needs a button can point at it."
        onOpen={onOpen} onDelete={onDelete} deleteTitle="Delete meeting link"
        rowFor={(c) => {
          const st = ctaStatus(c);
          const t = ctaType(c.type);
          const used = ctaUseSummary(ctaUses(c.id, { offers }));
          return (
            <>
              {/* Name over button text: the name is how she finds it, the button
                  text is what the public actually reads. Both, always. Not
                  ctaLabel: the button text has its own line, and falling back to
                  it would print it twice. */}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name || "Untitled link"}</div>
                <div style={{ fontSize: 11, color: B.dim, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.buttonText || "—"}</div>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, color: c.link ? B.mid : B.ghost, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.link || "No link yet"}
                </div>
                <div style={{ fontSize: 11, color: B.ghost, marginTop: 2 }}>{t.icon} {t.label}</div>
              </div>
              <div style={{ fontSize: 12, color: used ? B.mid : B.ghost }}>{used || "Not used yet"}</div>
              <div>
                <Status cls={st === "live" ? "locked" : "draft"} text={CTA_STATUS_LABEL[st]} />
              </div>
            </>
          );
        }}
      />
    </>
  );
}


/* The record screen: crumb, the LINK's name as the heading, the shared bar, and
   the rail of every link beside the one she is editing. Same shape as OfferCopy. */
export function CtaCopy({ ctas = [], selectedId, onSelect, view, setView, onBack, cta, children }) {
  const twoPane = useTwoPane();
  const t = ctaType(cta?.type);
  return (
    <>
      <Bread text="Run" crumb="Meeting Links" hint={CTA_HINT} />
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        <h2 style={{ ...pTitle, margin: 0 }}>{String(cta?.name || "").trim() || "New meeting link"}</h2>
        <Badge cls="neutral" text={`${t.icon} ${t.label}`} />
      </div>
      {/* On a phone RecordCopy draws the way back itself (levels={2}), so the
          bar's back stands down — the double "← All offers" of b8389d1. */}
      <MomentumBar view={view} setView={setView} copyLabel="Link" levels={2}
        lead={twoPane && <BarBack label="← All meeting links" onClick={onBack} />} />
      <RecordCopy
        levels={2} onExit={() => setView("table")} exitLabel="All meeting links"
        records={ctas} selectedId={selectedId} onSelect={onSelect}
        noun="meeting link" nounPlural="meeting links"
        emptyHint={<>No meeting links yet. Switch to <b style={nb}>Table</b> and add one.</>}
        rowFor={(c) => ({
          title: ctaLabel(c),
          meta: c.link ? ctaType(c.type).label : "No URL yet",
          dot: ctaDot(c),
          dotTitle: CTA_STATUS_LABEL[ctaStatus(c)],
        })}>
        {children}
      </RecordCopy>
    </>
  );
}


/* ── NOTHING TO PRESS (rule 1) ──────────────────────────────────────────────
   This was the last form in Momentum with Save / Cancel. Now the same two
   moments as the offer form:
     `commit` — typing. Writes on the way OUT of a field, not per keystroke.
     `setNow` — a choice (where it sends them, Draft / Live). There is no "way
                out" of a button, so it writes at once, passing the PATCH —
                setDraft is not synchronous, and saving straight after setting
                writes the value from before the click.
   A link needs a name OR button text to be saved: a row with neither would sit
   in every picker as "Untitled link". The footer says so in amber rather than
   staying silent. */
/* PICK FROM YOUR GHL CALENDARS (her ask, 2026-09-23; built 2026-10-09).
   Only where GHL is connected — without it the field is the paste box it
   always was, never a button that errors (her rule). Read only: the list comes
   from job `calendars` (api/_lib/ghlJobs/calendars.js), loaded when she opens
   it and kept for the visit. */
let calendarsCache = null;
function CalendarPick({ onPick }) {
  const { session } = useSession();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(calendarsCache ? { calendars: calendarsCache } : null);
  const load = async () => {
    setOpen((o) => !o);
    if (calendarsCache || state?.busy) return;
    setState({ busy: true });
    try {
      const token = await session?.getToken();
      const res = await fetch("/api/ghl", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ job: "calendars" }),
      });
      const r = await res.json().catch(() => ({}));
      if (r.ok && Array.isArray(r.calendars)) { calendarsCache = r.calendars; setState({ calendars: r.calendars }); }
      else setState({ error: r.error || "Your GHL calendars didn't load. Try again in a moment." });
    } catch {
      setState({ error: "Couldn't reach GHL just then. Try again in a moment." });
    }
  };
  return (
    <div style={{ marginTop: 8 }}>
      <button type="button" onClick={load} aria-expanded={open}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: `1px solid ${B.tealLine}`, borderRadius: 8, padding: "6px 11px", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, color: B.teal }}>
        <CalendarDays size={13} /> Pick from your GHL calendars
      </button>
      {open && (
        <div role="listbox" aria-label="Your GHL calendars" style={{ marginTop: 8, border: `1px solid ${B.border2}`, borderRadius: 10, background: B.surface, overflow: "hidden" }}>
          {state?.busy && <div style={{ padding: "12px 14px", fontSize: 12.5, color: B.dim }}>Loading your calendars…</div>}
          {state?.error && <div role="alert" style={{ padding: "12px 14px", fontSize: 12.5, color: B.amber }}>{state.error}</div>}
          {state?.calendars && state.calendars.length === 0 && <div style={{ padding: "12px 14px", fontSize: 12.5, color: B.dim }}>No calendars in GHL yet.</div>}
          {(state?.calendars || []).map((c) => (
            <div key={c.id} role="option" aria-selected={false} tabIndex={0}
              onClick={() => { onPick(c); setOpen(false); }}
              onKeyDown={(e) => { if (e.key === "Enter") { onPick(c); setOpen(false); } }}
              style={{ padding: "10px 14px", borderBottom: `1px solid ${B.border}`, cursor: "pointer", opacity: c.active ? 1 : 0.6 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: B.text }}>{c.name}{c.active ? "" : " · off in GHL"}</div>
              {c.description && <div style={{ fontSize: 11.5, color: B.dim, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.description}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CtaForm({ draft, setDraft, onSave, onDelete, saving, error, onDismissError }) {
  /* Above the return, or a form with no draft calls one hook fewer
     (src/hookOrder.test.js). */
  const crm = useCrmConnected();
  if (!draft) return null;
  const t = ctaType(draft.type);
  const preview = draft.buttonText || t.defaultButton;
  const canSave = !!(String(draft.name || "").trim() || String(draft.buttonText || "").trim());
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const commit = () => { if (canSave) onSave(); };
  const setNow = (k, v) => { setDraft((d) => ({ ...d, [k]: v })); if (canSave) onSave({ [k]: v }); };
  /* A CALENDAR PICKED FROM GHL fills the booking link and makes it a booking
     link; it names the link only if she hasn't, and says what it is only if
     she hasn't — her words are never overwritten by a calendar's. */
  const pickCalendar = (cal) => {
    const patch = calendarPatch(draft, cal);
    setDraft((d) => ({ ...d, ...patch }));
    if (canSave || patch.name) onSave(patch);
  };

  return (
    <>
      {error && (
        <div onClick={onDismissError} style={{ background: "var(--danger-quiet)", border: "1px solid var(--danger-line)", borderRadius: 9, padding: "11px 14px", marginBottom: 16, fontSize: 13, color: "var(--danger-text)", cursor: onDismissError ? "pointer" : "default", lineHeight: 1.55 }}>
          <b>This link didn't save.</b> {error} Your words are still on screen.
        </div>
      )}
      <div style={{ maxWidth: 560 }}>
        {/* Name first, button text SECOND — before the link. Button text is the
            only part of this form the public ever sees, so it doesn't belong
            below the plumbing. */}
        <Field label="Name">
          <input value={draft.name || ""} onChange={set("name")} onBlur={commit} placeholder="e.g. Discovery call — 30 minutes" style={input} />
          <Hint>Just for you — how you'll find it in the list. Never shown to anyone else.</Hint>
        </Field>

        <Field label="Button text">
          <input value={draft.buttonText || ""} onChange={set("buttonText")} onBlur={commit} placeholder={t.defaultButton} style={input} />
          <Hint>What people actually click.</Hint>
        </Field>

        {/* Not shown to anyone — it's what Brill reads. Without it a post can only
            append the button text as a closing line, which is why a CTA reads as
            bolted on rather than earned. */}
        <Field label="What is it?">
          <textarea value={draft.description || ""} onChange={set("description")} onBlur={commit}
            placeholder="A 10-minute quiz that names which of six operating styles someone runs on, and where it's costing them."
            style={{ ...input, minHeight: 68, resize: "vertical", lineHeight: 1.5 }} />
          <Hint>One sentence, for Brill — not shown publicly. This is what lets a post build toward the link instead of tacking it on the end.</Hint>
        </Field>

        <Field label={t.linkLabel}>
          <input value={draft.link || ""} onChange={set("link")} onBlur={commit} placeholder={t.placeholder} style={input} />
          <Hint>{t.linkHint}</Hint>
          {crm && <CalendarPick onPick={pickCalendar} />}
        </Field>

        {/* Switching type rewrites the copy above it, so it sits below the link
            it describes rather than above where it would look like a filter. */}
        <Field label="Where it sends them">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {CTA_TYPES.map((o) => (
              <Choice key={o.id} on={(draft.type || "url") === o.id} onClick={() => setNow("type", o.id)}>{o.icon} {o.label}</Choice>
            ))}
          </div>
        </Field>

        <Field label="Status">
          <div style={{ display: "flex", gap: 8 }}>
            <Choice on={(draft.status || "draft") === "draft"} onClick={() => setNow("status", "draft")}>Draft</Choice>
            <Choice on={draft.status === "live"} onClick={() => setNow("status", "live")}>Live</Choice>
          </div>
          <Hint>Status is about the link, not the copy — a link with no URL reads as <b style={nb}>No URL yet</b> whatever you pick here.</Hint>
        </Field>

        <Field label="Preview">
          <div style={{ border: `1px dashed ${B.border2}`, borderRadius: 10, padding: 20, textAlign: "center", background: B.bg }}>
            {/* Solid accent fill — needs --btn-*, since white on the dark
                canvas's #0AEFD3 accent is ~1.4:1. */}
            <span style={{ display: "inline-block", background: "var(--btn-bg)", color: "var(--btn-text)", padding: "10px 22px", borderRadius: 8, fontWeight: 700, fontSize: 13.5 }}>{preview} →</span>
            <span style={{ display: "block", fontSize: 11, color: B.faint, marginTop: 10 }}>
              {draft.link ? `→ ${draft.link}` : "→ no link yet"}
            </span>
          </div>
        </Field>
      </div>
      {/* The footer REPORTS the save rather than offering a button that
          promises it, and goes amber naming the reason when a save is blocked.
          The way back is in the bar. */}
      <RecordFooter saving={saving}
        problem={error ? "That didn't save — see the note above" : canSave ? "" : "Give it a name or button text to save it"}
        onDelete={draft.isNew ? null : () => onDelete(draft)} deleteLabel="Delete link" />
    </>
  );
}
